import { AlertTitle, Button, useToast } from '@chakra-ui/react';
import gql from 'graphql-tag';
import { DateTime } from 'luxon';
import { useMemo } from 'react';
import { InfoAlert } from '~components/ui/Alert';
import { useDestructiveAction } from '~components/ui/DestructiveAction';
import { fromError } from '~utils/errors';
import {
  AlertRebootScheduled_DisplayFragment,
  useAbortRebootJobMutation,
} from './__generated__/AlertRebootScheduled.graphql';

interface Props {
  display: AlertRebootScheduled_DisplayFragment;
}

export function AlertRebootScheduled({ display }: Props) {
  const rebootJob = useMemo(() => display.reboot?.latestJob, [display]);

  const toast = useToast();
  const [abortJob] = useAbortRebootJobMutation();
  const abortReboot = useDestructiveAction<{ jobId: string }>({
    title: 'Cancel reboot',
    message: 'Are you sure you want to cancel this reboot?',
    confirmLabel: 'Yes',
    onConfirm: async ({ jobId }) => {
      try {
        await abortJob({
          variables: {
            input: {
              jobId,
            },
          },
        });
      } catch (err) {
        toast({
          status: 'error',
          title: 'Cannot abort reboot',
          description: fromError(err, 'AbortReboot', {
            JOB_ALREADY_COMPLETED: 'The reboot is already completed.',
            JOB_ALREADY_REJECTED: 'The reboot failed.',
            JOB_ALREADY_ABORTED: 'The reboot is already aborted.',
            JOB_NOT_ABORTABLE: 'The reboot is not abortable.',
          }),
        });
      }
    },
  });

  if (
    rebootJob?.__typename !== 'RebootJobPlanned' &&
    rebootJob?.__typename !== 'RebootJobInProgress'
  )
    return null;

  const plannedAt = DateTime.fromISO(rebootJob?.plannedAt);
  return (
    <>
      <InfoAlert
        marginTop="10"
        actionButton={
          <Button
            variant="outline"
            size="sm"
            colorScheme="blue"
            color="blue.700"
            marginLeft="6"
            onClick={() => rebootJob && abortReboot.askConfirmation({ jobId: rebootJob.id })}
          >
            Cancel reboot
          </Button>
        }
      >
        <AlertTitle>
          {rebootJob?.__typename === 'RebootJobPlanned'
            ? `Reboot scheduled
                ${isToday(plannedAt) ? 'today' : `for ${plannedAt.toFormat('d MMM')}`}
                at
                ${plannedAt.toFormat('HH:mm')}`
            : rebootJob?.__typename === 'RebootJobInProgress'
            ? 'Rebooting…'
            : undefined}
        </AlertTitle>
      </InfoAlert>
      {abortReboot.confirmationNode}
    </>
  );
}

function isToday(date: DateTime): boolean {
  return date.toISODate() === DateTime.local().toISODate();
}

AlertRebootScheduled.graphql = {
  fragments: {
    AlertRebootScheduled_display: gql`
      fragment AlertRebootScheduled_display on Display {
        id
        reboot {
          latestJob {
            __typename
            id
            plannedAt
          }
        }
      }
    `,
  },
  mutations: {
    AbortRebootJob: gql`
      mutation AbortRebootJob($input: DisplayAbortJobInput!) {
        displayAbortJob(input: $input) {
          display {
            id
            ...AlertRebootScheduled_display
          }
        }
      }
    `,
  },
};
